import React, { useEffect, useState } from 'react';
import { Box, Button, Chip } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core';
import { Calendar, Home, Search, Star, Users, Globe } from 'react-feather';
import DirectionsCar from '@material-ui/icons/DriveEtaOutlined';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import useStore from 'src/hooks/useStore';
import { IStore } from 'src/interfaces/store';
import { TIME_RANGES } from '../../../views/reports/constants';
import useSource from 'src/hooks/useSource';
import {
    DEVELOPER_LEVEL_ACCESS,
    USER_LEVEL_ACCESS,
} from 'src/components/access';
import { getDocumentId } from 'src/utils/mongo/getDocumentId';
import { ISource } from 'src/interfaces/source';
import { groupSources } from '../utils/groupSources';
import { IFilters } from './types';
import { ButtonFilter } from './ButtonFilter';
import { cleanObject, getStoreAreasForUser } from '../utils';
import {
    BOTH_AREA,
    NEW_USER_AREA,
    PREOWNED_USER_AREA,
} from 'src/constants/store/areas';
import { groupStores } from '../utils/groupStores';
import { FilterHeader } from './FilterHeader';
import { Laptop } from '@material-ui/icons';
import useUser from 'src/hooks/useUser';
import { groupAgents } from '../utils/groupAgents';
import { IUser } from 'src/interfaces/user';
import { NoContent } from './NoContent';
import { DATE_RANGES } from 'src/views/charts/constants/timeRanges';
import { RangeCalendar } from 'src/components/Calendars';
import { RangeKeyDict } from 'react-date-range';
import moment from 'moment';
import clsx from 'clsx';
import { GroupedDataFilters } from './GroupedDataFilter';
import { mapOrigins } from 'src/components/Search/utils/mapOrigins';

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: theme.spacing(2),
        width: '100%',
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
        },
    },
    icon: {
        color: theme.palette.primary.main,
        height: 24,
        width: 24,
    },
    button: {
        width: '100%',
        textTransform: 'capitalize',
        height: 56,
        minWidth: 180,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    chips: {
        width: 110,
    },
    dateChips: {
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        maxWidth: 350,
    },
}));

export const Filters = ({
    currents,
    setCurrents,
    loadInformation,
    setOpen,
    className,
    buttonProps,
    disableAreaFilter = false,
    disableCarTypeFilter = false,
    disableStoreFilter = false,
    disableSourceFilter = false,
    disableFromFilter = false,
    disableAgentFilter = false,
    disableDateFilter = false,
    useStoreAreas = false,
    actionButtons,
    extraFilters,
    singleItem,
    ...props
}: IFilters) => {
    const classes = useStyles();
    const theme = useTheme();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { stores, getStores } = useStore();
    const { sources, getSourcesV2 } = useSource();
    const { agents, getAgents } = useUser();
    const [groupedAreas, setGroupedAreas] = useState<{
        allAreas: { _id: string; name: string }[];
    }>({
        allAreas: [],
    });
    const [groupedCarTypes, setGroupedCarTypes] = useState<{
        allCarTypes: { _id: string; name: string }[];
    }>({
        allCarTypes: [],
    });
    const [groupedStores, setGroupedStores] = useState<
        Record<string, IStore[]>
    >({});
    const [groupedSources, setGroupedSources] = useState<{
        digital: ISource[];
        showroom: ISource[];
    }>({
        digital: [],
        showroom: [],
    });
    const [groupedAgents, setGroupedAgents] = useState<Record<string, IUser[]>>(
        {}
    );
    const fromCategory = t('Leads.From');
    const groupedFrom = {
        [fromCategory]: mapOrigins(t).map((origin) => ({
            _id: origin.value,
            name: origin.label,
        })),
    };

    useEffect(() => {
        if (user?._id) {
            if (!stores?.length) getStores();

            let sourceFilters: {
                isActive: boolean;
                group?: string;
            } = {
                isActive: true,
            };

            if (user?.accessLevel !== DEVELOPER_LEVEL_ACCESS) {
                if (user?.group) {
                    sourceFilters.group = getDocumentId(user?.group);
                    getSourcesV2({ filters: sourceFilters });
                }
            } else {
                getSourcesV2({ filters: sourceFilters });
            }

            const canSeeNewCarType =
                user?.carType === BOTH_AREA || user?.carType === NEW_USER_AREA;
            const canSeePreownedCarType =
                user?.carType === BOTH_AREA ||
                user?.carType === PREOWNED_USER_AREA;

            let allCarTypes = [];

            if (canSeeNewCarType)
                allCarTypes.push({
                    _id: NEW_USER_AREA,
                    name: NEW_USER_AREA,
                });
            if (canSeePreownedCarType)
                allCarTypes.push({
                    _id: PREOWNED_USER_AREA,
                    name: PREOWNED_USER_AREA,
                });

            setGroupedCarTypes({ allCarTypes });

            const allAreas = useStoreAreas
                ? getStoreAreasForUser(user)
                : user?.areas.map((area: string) => ({
                      _id: area,
                      name: area,
                  }));
            setGroupedAreas({ allAreas: allAreas || [] });
        }
    }, [user, useStoreAreas]);

    useEffect(() => {
        if (sources) {
            const response = groupSources(sources);

            setGroupedSources(response);
        }
    }, [sources]);

    useEffect(() => {
        if (stores) {
            const response = groupStores(stores);
            setGroupedStores(response);
        }
    }, [stores]);

    useEffect(() => {
        if (disableAgentFilter) return;

        if (agents) {
            const response = groupAgents(agents, t);
            setGroupedAgents(response);
        }
    }, [agents, disableAgentFilter]);

    useEffect(() => {
        if (disableAgentFilter) return;

        if (
            currents.carType?.length > 0 ||
            currents.store?.length > 0 ||
            currents.type?.length > 0
        ) {
            let carTypes: string[] = [];

            if (currents.carType === NEW_USER_AREA)
                carTypes = [NEW_USER_AREA, BOTH_AREA];

            if (currents.carType === PREOWNED_USER_AREA)
                carTypes = [PREOWNED_USER_AREA, BOTH_AREA];

            let query = {
                stores: currents.store,
                areas: currents.type,
                carType: carTypes,
                select: 'name job accessLevel',
                accessLevel: USER_LEVEL_ACCESS,
            };

            const cleanedQuery = cleanObject(query);
            getAgents(cleanedQuery);
        }
    }, [
        currents.carType,
        currents.store,
        currents.type,
        disableAgentFilter,
    ]);

    const handleChangeCreatedAt = (value: string) => {
        let currentCreatedAt = {
            createdAt: value,
            startDate: value === 'custom' ? currents.startDate : '',
            endDate: value === 'custom' ? currents.endDate : '',
        };

        if (
            value === 'today' ||
            value === 'yesterday' ||
            value === 'month' ||
            value === 'pastMonth' ||
            value === 'year' ||
            value === 'pastYear'
        ) {
            currentCreatedAt = {
                ...currentCreatedAt,
                startDate: DATE_RANGES[value]?.gte || '',
                endDate: DATE_RANGES[value]?.lte || '',
            };
        }

        setCurrents({ ...currents, ...currentCreatedAt });
    };

    const handleChangeRange = (ranges: RangeKeyDict) => {
        const { startDate, endDate } = ranges.selection;

        setCurrents({
            ...currents,
            createdAt: 'custom',
            startDate: moment(startDate).startOf('day').format(),
            endDate: moment(endDate).endOf('day').format(),
        });
    };

    const isAnyAreaDisplayed = groupedAreas.allAreas.length > 0;
    const isAnyStoreDisplayed = stores?.length > 0;
    const isAnySourceDisplayed = sources?.length > 0;
    const isAnyAgentDisplayed = agents?.length > 0;
    const isAnyCarTypeDisplayed = groupedCarTypes.allCarTypes.length > 0;

    return (
        <Box className={clsx(classes.root, className)} {...props}>
            <ButtonFilter
                type="type"
                title={t('ModalFilters.area')}
                currents={currents}
                setCurrents={setCurrents}
                startIcon={<Laptop className={classes.icon} />}
                content={
                    isAnyAreaDisplayed ? (
                        <GroupedDataFilters
                            type="type"
                            categories={['allAreas']}
                            groupedData={groupedAreas}
                            currents={currents}
                            setCurrents={setCurrents}
                            translationKey="ModalFilters.summaryValues.type"
                            singleItem={singleItem?.area}
                        />
                    ) : (
                        <NoContent type="type" />
                    )
                }
                {...buttonProps}
            />

            {!disableCarTypeFilter && (
                <ButtonFilter
                    type="carType"
                    title={t('ModalFilters.carType')}
                    currents={currents}
                    setCurrents={setCurrents}
                    startIcon={<DirectionsCar className={classes.icon} />}
                    content={
                        isAnyCarTypeDisplayed ? (
                            <GroupedDataFilters
                                type="carType"
                                categories={['allCarTypes']}
                                groupedData={groupedCarTypes}
                                currents={currents}
                                setCurrents={setCurrents}
                                translationKey="ModalFilters.summaryValues.carType"
                                singleItem={singleItem?.carType}
                            />
                        ) : (
                            <NoContent type="carType" />
                        )
                    }
                    {...buttonProps}
                />
            )}

            {!disableStoreFilter && (
                <ButtonFilter
                    type="store"
                    title={t('ModalFilters.store')}
                    currents={currents}
                    setCurrents={setCurrents}
                    startIcon={<Home className={classes.icon} />}
                    content={
                        isAnyStoreDisplayed ? (
                            <GroupedDataFilters
                                type="store"
                                categories={Object.keys(groupedStores)}
                                groupedData={groupedStores}
                                currents={currents}
                                setCurrents={setCurrents}
                                singleItem={singleItem?.store}
                            />
                        ) : (
                            <NoContent type="store" />
                        )
                    }
                    {...buttonProps}
                />
            )}

            {!disableSourceFilter && (
                <ButtonFilter
                    type="source"
                    title={t('ModalFilters.source')}
                    currents={currents}
                    setCurrents={setCurrents}
                    startIcon={<Star className={classes.icon} />}
                    content={
                        isAnySourceDisplayed ? (
                            <GroupedDataFilters
                                type="source"
                                categories={Object.keys(groupedSources)}
                                groupedData={groupedSources}
                                currents={currents}
                                setCurrents={setCurrents}
                                singleItem={singleItem?.source}
                            />
                        ) : (
                            <NoContent type="source" />
                        )
                    }
                    {...buttonProps}
                />
            )}

            {!disableFromFilter && (
                <ButtonFilter
                    type="from"
                    title={t('Leads.From')}
                    currents={currents}
                    setCurrents={setCurrents}
                    startIcon={<Globe className={classes.icon} />}
                    content={
                        <GroupedDataFilters
                            type="from"
                            categories={[fromCategory]}
                            groupedData={groupedFrom}
                            currents={currents}
                            setCurrents={setCurrents}
                            singleItem={singleItem?.from}
                        />
                    }
                    {...buttonProps}
                />
            )}

            {!disableAgentFilter && user?.accessLevel !== USER_LEVEL_ACCESS && (
                <ButtonFilter
                    type="agent"
                    title={t('ModalFilters.agent')}
                    currents={currents}
                    setCurrents={setCurrents}
                    startIcon={<Users className={classes.icon} />}
                    content={
                        isAnyAgentDisplayed ? (
                            <GroupedDataFilters
                                type="agent"
                                categories={Object.keys(groupedAgents)}
                                groupedData={groupedAgents}
                                currents={currents}
                                setCurrents={setCurrents}
                                singleItem={singleItem?.agent}
                            />
                        ) : (
                            <NoContent type="agent" />
                        )
                    }
                    {...buttonProps}
                />
            )}

            {!disableDateFilter && (
                <ButtonFilter
                    type="createdAt"
                    title={
                        currents.createdAt
                            ? t(`Calendar.${currents.createdAt}`)
                            : t('ModalFilters.date')
                    }
                    currents={currents}
                    setCurrents={setCurrents}
                    startIcon={<Calendar className={classes.icon} />}
                    cardStyle={{
                        minWidth: 200,
                    }}
                    content={
                        <Box className={classes.content}>
                            <FilterHeader title={t('Comments.Date')} />
                            <Box className={classes.dateChips}>
                                {TIME_RANGES.map((range) => (
                                    <Chip
                                        onClick={() =>
                                            handleChangeCreatedAt(range.label)
                                        }
                                        className={classes.chips}
                                        label={String(
                                            t(`Calendar.${range.label}`)
                                        )}
                                        color="primary"
                                        variant={
                                            currents.createdAt === range.label
                                                ? 'default'
                                                : 'outlined'
                                        }
                                    />
                                ))}
                            </Box>
                            <RangeCalendar
                                ranges={[
                                    {
                                        startDate: moment(
                                            currents.startDate
                                        ).toDate(),
                                        endDate: moment(
                                            currents.endDate
                                        ).toDate(),
                                        key: 'selection',
                                        color: theme.palette.primary.main,
                                    },
                                ]}
                                onChange={handleChangeRange}
                            />
                        </Box>
                    }
                    {...buttonProps}
                />
            )}

            {extraFilters}

            <Button
                color="primary"
                variant="contained"
                onClick={() => {
                    loadInformation();
                    setOpen?.(false);
                }}
                className={classes.button}
                startIcon={<Search />}
                id="search-button"
                {...buttonProps}
            >
                {t('Buttons.Search')}
            </Button>

            {actionButtons &&
                actionButtons.map((button, index) => (
                    <Button
                        key={index}
                        color="primary"
                        variant="contained"
                        className={classes.button}
                        {...buttonProps}
                        {...button}
                    >
                        {button.label}
                    </Button>
                ))}
        </Box>
    );
};
